/**
 * Onboarding Modal
 * Multi-step onboarding flow that appears after signup
 */

import { useState, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useUpdateProfile, useCompleteOnboarding, useAddCourse, useUpdateUserProfile } from '@/hooks/use-learning-profile';
import { useAnalytics } from '@/hooks/use-analytics';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/lib/auth';
import { emitSignal, createStatusDeclaredSignal, createGoalDeclaredSignal, createProfileDeclaredSignal } from '@/lib/signals';
import type { CurrentStatus, GoalMode } from '@/types/learning-profile';
import { LEGAL_SUBJECTS } from '@/types/learning-profile';
import { BookOpen, Target, GraduationCap, Scale, Briefcase, HelpCircle, Check } from 'lucide-react';

interface OnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

type OnboardingStep = 'name' | 'status' | 'goal' | 'courses' | 'complete';

const STATUS_OPTIONS: { value: CurrentStatus; label: string; description: string; icon: React.ReactNode }[] = [
  { value: '1L', label: '1L', description: 'First year of law school', icon: <BookOpen className="h-5 w-5" /> },
  { value: '2L', label: '2L', description: 'Second year', icon: <BookOpen className="h-5 w-5" /> },
  { value: '3L', label: '3L', description: 'Third year', icon: <GraduationCap className="h-5 w-5" /> },
  { value: 'bar_prep', label: 'Bar Prep', description: 'Studying for the bar exam', icon: <Scale className="h-5 w-5" /> },
  { value: 'attorney', label: 'Attorney', description: 'Licensed, practicing or refreshing', icon: <Briefcase className="h-5 w-5" /> },
  { value: 'other', label: 'Other', description: 'Something else', icon: <HelpCircle className="h-5 w-5" /> },
];

const GOAL_OPTIONS: { value: GoalMode; label: string; description: string; forStatus: CurrentStatus[] }[] = [
  { value: 'cold_calls', label: 'Cold Call Prep', description: 'Be ready for class participation', forStatus: ['1L', '2L', '3L'] },
  { value: 'finals', label: 'Finals Prep', description: 'Prepare for law school exams', forStatus: ['1L', '2L', '3L'] },
  { value: 'writing_improvement', label: 'Writing Improvement', description: 'Strengthen legal analysis skills', forStatus: ['1L', '2L', '3L', 'attorney', 'llm'] },
  { value: 'bar', label: 'Bar Exam', description: 'Pass the bar exam', forStatus: ['bar_prep'] },
  { value: 'fundamentals', label: 'Fundamentals', description: 'Master core legal concepts', forStatus: ['1L', '2L', '3L', 'other', 'unknown'] },
  { value: 'general', label: 'Just Exploring', description: 'No specific goal right now', forStatus: ['1L', '2L', '3L', 'bar_prep', 'attorney', 'llm', 'other', 'unknown'] },
];

export function OnboardingModal({ open, onComplete }: OnboardingModalProps) {
  const [step, setStep] = useState<OnboardingStep>('name');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<CurrentStatus | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<GoalMode | null>(null);
  const [selectedCourses, setSelectedCourses] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateProfile = useUpdateProfile();
  const updateUserProfile = useUpdateUserProfile();
  const completeOnboarding = useCompleteOnboarding();
  const addCourse = useAddCourse();
  const { trackEvent } = useAnalytics();
  const { toast } = useToast();
  const { user } = useAuth();

  const totalSteps = 4;
  const currentStepNumber = step === 'name' ? 1 : step === 'status' ? 2 : step === 'goal' ? 3 : step === 'courses' ? 4 : 4;
  const progress = (currentStepNumber / totalSteps) * 100;

  const handleStatusSelect = useCallback(async (status: CurrentStatus) => {
    setSelectedStatus(status);
    trackEvent('onboarding.question.answered', { question: 'current_status', value: status });
  }, [trackEvent]);

  const handleGoalSelect = useCallback(async (goal: GoalMode) => {
    setSelectedGoal(goal);
    trackEvent('onboarding.question.answered', { question: 'goal_mode', value: goal });
  }, [trackEvent]);

  const handleCourseToggle = useCallback((subject: string) => {
    setSelectedCourses(prev =>
      prev.includes(subject)
        ? prev.filter(s => s !== subject)
        : [...prev, subject]
    );
  }, []);

  // Save whatever data has been entered on the current step
  const saveCurrentStepData = useCallback(async () => {
    if (step === 'name' && firstName.trim()) {
      await updateUserProfile.mutateAsync({
        first_name: firstName.trim(),
        last_name: lastName.trim() || null
      });
    } else if (step === 'status' && selectedStatus) {
      await updateProfile.mutateAsync({ current_status: selectedStatus });
    } else if (step === 'goal' && selectedGoal) {
      await updateProfile.mutateAsync({ goal_mode: selectedGoal });
    } else if (step === 'courses' && selectedCourses.length > 0) {
      // Use allSettled so already-existing courses (unique constraint) don't abort the save
      await Promise.allSettled(
        selectedCourses.map(subject => addCourse.mutateAsync({ subject }))
      );
    }
  }, [step, firstName, lastName, selectedStatus, selectedGoal, selectedCourses, updateUserProfile, updateProfile, addCourse]);

  const handleNext = useCallback(async () => {
    try {
      if (step === 'name' && firstName.trim()) {
        await updateUserProfile.mutateAsync({
          first_name: firstName.trim(),
          last_name: lastName.trim() || null
        });
        trackEvent('onboarding.question.answered', { question: 'name', hasLastName: !!lastName.trim() });
        setStep('status');
      } else if (step === 'status' && selectedStatus) {
        await updateProfile.mutateAsync({ current_status: selectedStatus });
        if (user?.id) emitSignal(createStatusDeclaredSignal(user.id, selectedStatus));
        setStep('goal');
      } else if (step === 'goal' && selectedGoal) {
        await updateProfile.mutateAsync({ goal_mode: selectedGoal });
        if (user?.id) emitSignal(createGoalDeclaredSignal(user.id, selectedGoal, selectedStatus || 'unknown'));
        // If bar_prep, skip courses step
        if (selectedStatus === 'bar_prep') {
          setStep('complete');
        } else {
          setStep('courses');
        }
      } else if (step === 'courses') {
        // Save courses immediately so they persist even if user bails on the summary screen
        if (selectedCourses.length > 0) {
          await Promise.all(
            selectedCourses.map(subject => addCourse.mutateAsync({ subject }))
          );
        }
        setStep('complete');
      }
    } catch (error) {
      console.error('Failed to save onboarding progress:', error);
      toast({
        title: 'Error',
        description: 'Failed to save your changes. Please try again.',
        variant: 'destructive',
      });
    }
  }, [step, firstName, lastName, selectedStatus, selectedGoal, selectedCourses, updateProfile, updateUserProfile, addCourse, trackEvent, toast, user]);

  const handleComplete = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Courses already saved when advancing from step 4 — just mark onboarding complete
      await completeOnboarding.mutateAsync(false);

      // Emit full profile declaration signal for Intelligence cold start
      if (user?.id) {
        emitSignal(createProfileDeclaredSignal(user.id, {
          current_status: selectedStatus || 'unknown',
          goal_mode: selectedGoal || 'general',
          courses: selectedCourses,
        }));
      }

      trackEvent('onboarding.complete', {
        status: selectedStatus,
        goal: selectedGoal,
        coursesCount: selectedCourses.length,
      });

      onComplete();
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      toast({
        title: 'Error',
        description: 'Failed to complete setup. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedCourses, selectedStatus, selectedGoal, completeOnboarding, trackEvent, onComplete, toast, user]);

  const handleSkip = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Save whatever the user has entered on the current step before skipping
      await saveCurrentStepData();
      await completeOnboarding.mutateAsync(true);
      trackEvent('onboarding.skipped', { step });
      onComplete();
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
      toast({
        title: 'Error',
        description: 'Failed to skip. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, saveCurrentStepData, completeOnboarding, step, trackEvent, onComplete, toast]);

  const filteredGoals = useMemo(
    () => GOAL_OPTIONS.filter(
      goal => !selectedStatus || goal.forStatus.includes(selectedStatus)
    ),
    [selectedStatus]
  );

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-lg p-0 gap-0 overflow-hidden [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Welcome to JD Simplified</DialogTitle>
        {/* Progress Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">
              Step {currentStepNumber} of {totalSteps}
            </span>
            <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground hover:text-foreground">
              Skip for now
            </Button>
          </div>
          <Progress value={progress} className="h-1" />
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Step 1: Name */}
          {step === 'name' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold">Welcome! What's your name?</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  We'll use this to personalize your experience
                </p>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First name <span className="text-destructive">*</span></Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Enter your first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    autoFocus
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName">Last name <span className="text-muted-foreground text-xs">(optional)</span></Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Enter your last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="h-12"
                  />
                </div>
              </div>

              <Button
                onClick={handleNext}
                disabled={!firstName.trim()}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 2: Status */}
          {step === 'status' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold">What are you studying for?</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  This helps us personalize your experience
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Select your status">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    role="radio"
                    aria-checked={selectedStatus === option.value}
                    onClick={() => handleStatusSelect(option.value)}
                    className={cn(
                      "flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all",
                      "hover:border-primary hover:bg-primary/5",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      selectedStatus === option.value
                        ? "border-primary bg-primary/10"
                        : "border-border"
                    )}
                  >
                    <div className={cn(
                      "mb-2 p-2 rounded-full",
                      selectedStatus === option.value ? "bg-primary/20 text-primary" : "bg-muted"
                    )}>
                      {option.icon}
                    </div>
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground text-center mt-1">
                      {option.description}
                    </span>
                  </button>
                ))}
              </div>

              <Button
                onClick={handleNext}
                disabled={!selectedStatus}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 3: Goal */}
          {step === 'goal' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold">What&apos;s your main goal?</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  We&apos;ll tailor recommendations based on this
                </p>
              </div>

              <div className="space-y-2" role="radiogroup" aria-label="Select your goal">
                {filteredGoals.map((option) => (
                  <button
                    key={option.value}
                    role="radio"
                    aria-checked={selectedGoal === option.value}
                    onClick={() => handleGoalSelect(option.value)}
                    className={cn(
                      "flex items-center w-full p-4 rounded-lg border-2 transition-all text-left",
                      "hover:border-primary hover:bg-primary/5",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      selectedGoal === option.value
                        ? "border-primary bg-primary/10"
                        : "border-border"
                    )}
                  >
                    <div className={cn(
                      "mr-3 p-1.5 rounded-full",
                      selectedGoal === option.value ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      {selectedGoal === option.value ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Target className="h-4 w-4" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium">{option.label}</span>
                      <p className="text-sm text-muted-foreground">{option.description}</p>
                    </div>
                  </button>
                ))}
              </div>

              <Button
                onClick={handleNext}
                disabled={!selectedGoal}
                className="w-full"
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step 4: Courses */}
          {step === 'courses' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-xl font-semibold">What courses are you taking?</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Select your current semester courses (optional)
                </p>
              </div>

              <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-1" role="group" aria-label="Select your courses">
                {LEGAL_SUBJECTS.map((subject) => (
                  <button
                    key={subject.value}
                    aria-pressed={selectedCourses.includes(subject.value)}
                    onClick={() => handleCourseToggle(subject.value)}
                    className={cn(
                      "inline-flex items-center px-3 py-2 rounded-full text-sm transition-all min-h-[44px]",
                      "border hover:border-primary",
                      "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
                      selectedCourses.includes(subject.value)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border"
                    )}
                  >
                    {selectedCourses.includes(subject.value) && (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    {subject.label}
                  </button>
                ))}
              </div>

              {selectedCourses.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {selectedCourses.length} course{selectedCourses.length !== 1 ? 's' : ''} selected
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep('complete')}
                  className="flex-1"
                >
                  Skip
                </Button>
                <Button
                  onClick={handleNext}
                  className="flex-1"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {/* Step 5: Complete */}
          {step === 'complete' && (
            <div className="space-y-6 text-center py-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Check className="h-8 w-8 text-primary" />
              </div>

              <div>
                <h2 className="text-xl font-semibold">You&apos;re all set, {firstName}!</h2>
                <p className="text-muted-foreground mt-2">
                  We&apos;ll learn more about you as you practice. Your experience will get better over time.
                </p>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 text-left">
                <h3 className="font-medium mb-2">Your profile:</h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>Name: <span className="text-foreground">{firstName}{lastName ? ` ${lastName}` : ''}</span></li>
                  <li>Status: <span className="text-foreground">{selectedStatus?.toUpperCase() || 'Not set'}</span></li>
                  <li>Goal: <span className="text-foreground">{selectedGoal?.replace('_', ' ') || 'General'}</span></li>
                  {selectedCourses.length > 0 && (
                    <li>Courses: <span className="text-foreground">{selectedCourses.length} selected</span></li>
                  )}
                </ul>
              </div>

              <Button
                onClick={handleComplete}
                disabled={isSubmitting}
                className="w-full"
                size="lg"
              >
                {isSubmitting ? 'Setting up...' : 'Get Started'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingModal;
